import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, IconButton, Chip, Button, Skeleton} from '@mui/material'
import {Menu as MenuIcon, MoreHoriz, WarningAmberOutlined, CheckCircleOutline} from '@mui/icons-material'
import {accountApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useMobile} from '../../hooks'
import {parseError} from '../../utils/helpers'
import {SkeletonContainer, TextSkeleton, ChipSkeleton, ButtonSkeleton} from '../../components'
import {UpdatePayor} from './components'

const evvChip = required =>
    required ? (
        <Chip label="EVV Required" variant="outlined" color="success" size="small" />
    ) : (
        <Chip label="No EVV" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider'}} />
    )

const payorStatusChip = connected =>
    connected ? (
        <Chip icon={<CheckCircleOutline fontSize="small" />} label="Connected" variant="outlined" color="success" size="small" />
    ) : (
        <Chip label="Credentials not set" variant="outlined" color="error" size="small" />
    )

// Two branch cards laid out like the loaded ones.
const BranchesSkeleton = ({mobile}) => (
    <SkeletonContainer>
        {[0, 1].map(index => (
            <div key={index}>
                <br />
                <div className="card">
                    <div className="top">
                        <div className="top-left">
                            <TextSkeleton width={index ? 130 : 170} fontSize={16} />
                            <TextSkeleton width={80} fontSize={12} />
                        </div>
                        <div className="top-right">
                            <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                        </div>
                    </div>
                    <br />
                    <div className="content-container">
                        {mobile ? (
                            <>
                                <div className="content">
                                    <TextSkeleton width="70%" />
                                </div>
                                <br />
                                <div className="content">
                                    <TextSkeleton width="55%" />
                                </div>
                            </>
                        ) : (
                            <div className="content">
                                <TextSkeleton width={index ? 300 : 360} />
                            </div>
                        )}
                        <br />
                        <div className="content">
                            <TextSkeleton width={mobile ? '60%' : 240} />
                        </div>
                        <br />
                        <div className="field-stack">
                            <TextSkeleton width={120} fontSize={16} />
                            <TextSkeleton width={mobile ? '85%' : 340} />
                        </div>
                        <br />
                        <div className="field-stack">
                            <TextSkeleton width={60} fontSize={12} />
                            <div className="services-row">
                                {[0, 1].map(service => (
                                    <div className="service-box" key={service}>
                                        <TextSkeleton width={service ? 80 : 110} />
                                        <ChipSkeleton width={96} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <br />
                        <div className="field-stack">
                            <TextSkeleton width={56} fontSize={12} />
                            <div className="payors-list">
                                {[0, 1].map(payer => (
                                    <div className="payor-row" key={payer}>
                                        <div className="payor-info">
                                            <TextSkeleton width={payer ? 110 : 150} />
                                            <TextSkeleton width={90} />
                                        </div>
                                        <div className="payor-actions">
                                            <ChipSkeleton width={96} />
                                            <ButtonSkeleton width={130} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const [open, setOpen] = useState(false)
    const [selectedData, setSelectedData] = useState(null)
    const {setMessageBox, setMobileSidebar} = useGlobalStates()
    const {mobile} = useMobile()
    const [branches, setBranches] = useState([])
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        if (mobile) {
            setMobileSidebar(false)
        }

        accountApi
            .getBranches()
            .then(
                ({
                    data: {
                        data: {branches},
                    },
                }) => {
                    setBranches(branches)
                    setSkeleton(false)
                },
            )
            .catch(error => {
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    const openPayorModal = (branch, payer) => {
        setSelectedData({branch, payer})
        setOpen(true)
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <div className="title">
                            <IconButton onClick={() => setMobileSidebar(true)}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h5" color="text.primary">
                                Branch Management
                            </Typography>
                        </div>
                    </div>
                    <br />
                </>
            ) : (
                <Typography variant="h5" color="text.primary">
                    Branch Management
                </Typography>
            )}
            <br />
            {skeleton && <BranchesSkeleton mobile={mobile} />}
            {!skeleton &&
                branches?.map(branch => {
                    const disconnectedCount = branch?.payers?.filter(payer => !payer?.username)?.length || 0

                    return (
                        <div key={branch.id}>
                            <br />
                            <div className="card">
                                <div className="top">
                                    <div className="top-left">
                                        <Typography variant="h8" color="primary">
                                            {branch?.branch_name}
                                        </Typography>
                                        <Typography variant="h10" color="text.primary">
                                            {branch?.provider_number}
                                        </Typography>
                                    </div>
                                    <div className="top-right">
                                        {disconnectedCount > 0 && (
                                            <Chip
                                                icon={<WarningAmberOutlined fontSize="small" />}
                                                label={`${disconnectedCount} payor${disconnectedCount > 1 ? 's' : ''} not connected`}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                            />
                                        )}
                                        <IconButton>
                                            <MoreHoriz />
                                        </IconButton>
                                    </div>
                                </div>
                                <br />
                                <div className="content-container">
                                    {mobile ? (
                                        <>
                                            <div className="content">
                                                <Typography variant="h8" color="text.primary">
                                                    Email:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.email}
                                                </Typography>
                                            </div>
                                            <br />
                                            <div className="content">
                                                <Typography variant="h9" color="text.primary">
                                                    Phone Number:
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {branch?.phone_number}
                                                </Typography>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="content">
                                            <Typography variant="h8" color="text.primary">
                                                Email:
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {branch?.email}
                                            </Typography>
                                            <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                                |
                                            </Typography>
                                            <Typography variant="h9" color="text.primary">
                                                Phone Number:
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {branch?.phone_number}
                                            </Typography>
                                        </div>
                                    )}
                                    <br />
                                    <div className="content">
                                        <Typography variant="h8" color="text.primary">
                                            Branch NPI:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {branch?.npi || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                            |
                                        </Typography>
                                        <Typography variant="h9" color="text.primary">
                                            UMPI:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {branch?.umpi || '—'}
                                        </Typography>
                                    </div>
                                    <br />
                                    <div className="field-stack">
                                        <Typography variant="h8" color="text.primary">
                                            Branch Address:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {`${branch?.addresses?.[0]?.street_line1}${
                                                branch?.addresses?.[0]?.street_line2 ? ', ' + branch?.addresses?.[0]?.street_line2 : ''
                                            }, ${branch?.addresses?.[0]?.city}, ${branch?.addresses?.[0]?.state}, ${branch?.addresses?.[0]?.zip_code}`}
                                        </Typography>
                                    </div>
                                    <br />
                                    <div className="field-stack">
                                        <Typography variant="overline" color="text.secondary" className="section-label">
                                            Services
                                        </Typography>
                                        <div className="services-row">
                                            {branch?.services?.map(service => (
                                                <div className="service-box" key={service.id}>
                                                    <Typography variant="h9" color="text.primary">
                                                        {service?.name}
                                                    </Typography>
                                                    {evvChip(service?.evv_required)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <br />
                                    <div className="field-stack">
                                        <Typography variant="overline" color="text.secondary" className="section-label">
                                            Payors
                                        </Typography>
                                        <div className="payors-list">
                                            {branch?.payers?.map(payer => {
                                                const connected = !!payer?.username

                                                return (
                                                    <div className="payor-row" key={payer.id}>
                                                        <div className="payor-info">
                                                            <Typography variant="h9" color="text.primary">
                                                                {payer?.payer_name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                ID: {payer?.provider_number}
                                                            </Typography>
                                                        </div>
                                                        <div className="payor-actions">
                                                            {payorStatusChip(connected)}
                                                            <Button
                                                                variant="contained"
                                                                color={connected ? 'primary' : 'error'}
                                                                onClick={() => openPayorModal(branch, payer)}>
                                                                {connected ? 'Update payor' : 'Add Credentials'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <UpdatePayor open={open} setOpen={setOpen} data={selectedData} />
        </>
    )
}
